import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, Sparkles, Calendar, ShieldCheck, Vault } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMembership } from '@/contexts/MembershipContext';
import { MEMBERSHIP_PLANS } from '@/lib/business/KinsCircleBusinessEngine';

// ─────────────────────────────────────────────────────────────────────────────
// UnpaidDashboardGate
//
// Renders the "subscribe to unlock your family circle" panel that REPLACES the
// authenticated-only sections of the dashboard (FamilyGraphSection, activity
// feed, gallery, message board, events, news, AI cards) when the signed-in
// user is NOT yet on a paid plan and is NOT staff.
//
// Strictly access-gating UI. Does NOT:
//   • mutate family/tree/relationship/invite/upload data
//   • change pricing amounts, branding, auth provider, or DB schema
//   • run any database query
//
// It only:
//   • Surfaces the two real CTAs ("Choose Family monthly" → monthly, "Get
//     Founder's yearly" → yearly) which already route through the existing
//     MembershipModal (cycle-locked) by calling openMembershipModal(cycle).
//   • Points users at the existing /account page for billing details.
// ─────────────────────────────────────────────────────────────────────────────

const UnpaidDashboardGate: React.FC = () => {
  const { openMembershipModal } = useMembership();
  const navigate = useNavigate();
  const familyPlan = MEMBERSHIP_PLANS.family_10;

  return (
    <section
      id="graph"
      className="relative py-16 px-4 bg-gradient-to-br from-amber-50 via-white to-fuchsia-50"
    >
      <div className="max-w-4xl mx-auto">
        <div className="rounded-3xl border-2 border-amber-300 bg-white shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-800 via-amber-900 to-green-900 text-amber-50 px-8 py-10 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-50/10 backdrop-blur border border-amber-300/30 flex items-center justify-center mx-auto mb-4">
              <Vault className="w-8 h-8 text-amber-200" />
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300/80 mb-2">
              KinsCircle Premium
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3">
              Your family circle is waiting
            </h2>
            <p className="text-amber-100/85 text-sm md:text-base max-w-xl mx-auto">
              Activate your membership to open your family tree, save relatives,
              upload memories, send invites, and unlock everything KinsCircle
              has to offer.
            </p>
          </div>

          {/* Plan cards */}
          <div className="p-6 md:p-8 grid md:grid-cols-2 gap-4">
            {/* Monthly */}
            <button
              type="button"
              onClick={() => openMembershipModal('family_10', 'monthly')}
              className="text-left rounded-2xl border-2 border-fuchsia-200 hover:border-fuchsia-400 bg-white p-5 transition-all shadow-sm hover:shadow-lg group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-fuchsia-600" />
                </div>
                <div className="font-serif text-lg font-bold text-amber-950">
                  Monthly
                </div>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-serif text-3xl font-bold text-amber-950">
                  ${familyPlan.monthlyPrice.toFixed(2)}
                </span>
                <span className="text-xs text-amber-800/70">/month</span>
              </div>
              <div className="text-xs text-emerald-700 font-semibold mb-3">
                10 family seats · billed monthly
              </div>
              <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-700 group-hover:text-fuchsia-900">
                <Sparkles className="w-4 h-4" />
                Start free trial
              </div>
            </button>

            {/* Yearly — Founder's */}
            <button
              type="button"
              onClick={() => openMembershipModal('family_10', 'yearly')}
              className="relative text-left rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-5 transition-all shadow-md hover:shadow-xl group"
            >
              <div className="absolute -top-3 left-5 bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white text-[10px] uppercase tracking-widest px-3 py-0.5 rounded-full font-bold shadow">
                Best Value
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-700" />
                </div>
                <div className="font-serif text-lg font-bold text-amber-950">
                  Yearly · Family
                </div>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-serif text-3xl font-bold text-amber-950">
                  ${familyPlan.yearlyPrice.toFixed(2)}
                </span>
                <span className="text-xs text-amber-800/70">/year</span>
              </div>
              <div className="text-xs text-amber-800/70 mb-3">
                Charged today · renews yearly · cancel anytime
              </div>
              <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 group-hover:text-amber-950">
                <Crown className="w-4 h-4" />
                Choose Family yearly
              </div>
            </button>
          </div>

          {/* What unlocks */}
          <div className="px-6 md:px-8 pb-6 md:pb-8">
            <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-5">
              <div className="text-xs uppercase tracking-widest text-amber-800/70 mb-3 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                What unlocks the moment your payment succeeds
              </div>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-amber-950">
                <li className="flex gap-2">
                  <Sparkles className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  Save relatives to your family tree
                </li>
                <li className="flex gap-2">
                  <Sparkles className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  Upload photos, videos & memories
                </li>
                <li className="flex gap-2">
                  <Sparkles className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  Invite family members
                </li>
                <li className="flex gap-2">
                  <Sparkles className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  Family activity feed, gallery & events
                </li>
              </ul>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-xs text-amber-800/70">
                <Lock className="w-3.5 h-3.5" />
                Secure checkout · cancel anytime
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/settings')}
                className="border-amber-300 text-amber-900 hover:bg-amber-50"
              >
                Manage account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnpaidDashboardGate;
